'use client';

import {
	IncentivesConfigCard,
	type IncentivesConfigCardData,
	IncentivesConfigCardSkeleton,
	IncentivesGseView,
	IncentivesPageView,
} from '@slims/ui/organisms';
import { StatusBlock } from '@slims/ui/molecules';
import { periodMonth } from '@slims/ui/lib';
import { useMemo, useState } from 'react';
import { useEnergyData } from '@/hooks/use-energy-data';
import { useMembers } from '@/hooks/use-members';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { useSogliaPreview } from '@/hooks/use-soglia-preview';
import { useConfigToggle } from '@/hooks/use-config-toggle';

/**
 * Finestra ISO del periodo selezionato: sempre il mese intero, anche quello in
 * corso, come la soglia di incentivabilità che è mensile.
 */
function rangeForPeriod(period: string | null): { from: string; to: string } {
	const now = new Date();
	const [year, month] = period
		? (period.split('-').map(Number) as [number, number])
		: [now.getUTCFullYear(), now.getUTCMonth() + 1];
	return {
		from: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
		to: new Date(Date.UTC(year, month, 1)).toISOString(),
	};
}

/**
 * Data wiring for the GSE incentives page. Presentation lives in @slims/ui
 * (IncentivesGseView). Come Dashboard, Monitoraggio e Previsione, con più
 * configurazioni in ambito compare il toggle «Panoramica»/«Configurazione
 * singola»: la Panoramica è una card per configurazione, il clic apre il
 * pannello completo di quella.
 */
export default function IncentivesPage() {
	const { scopes, loading: scopesLoading, effectiveLeaf, openConfig, toggle, showOverview } =
		useConfigToggle();

	if (!effectiveLeaf) {
		return (
			<StatusBlock
				kind={scopesLoading ? 'loading' : 'empty'}
				size="lg"
				message={
					scopesLoading
						? 'Caricamento...'
						: 'Nessuna configurazione selezionata. Scegli una vista dal selettore in alto.'
				}
			/>
		);
	}

	return (
		<IncentivesPageView
			{...toggle}
			overviewGridSlot={
				showOverview
					? scopes.map((leaf) => (
							<IncentivesConfigCardWiring
								key={leaf.orgId}
								leaf={leaf}
								onOpen={() => openConfig(leaf.orgId)}
							/>
						))
					: undefined
			}
			content={
				<IncentivesPanel key={effectiveLeaf.orgId} orgId={effectiveLeaf.orgId} />
			}
		/>
	);
}

/** Alimenta una card della «Panoramica»: i numeri del mese in corso di una configurazione sola. */
function IncentivesConfigCardWiring({ leaf, onOpen }: { leaf: ScopeLeaf; onOpen: () => void }) {
	// Mese in corso: la Panoramica non ha un selettore di periodo, la finestra
	// temporale la scelgono le schede del pannello una volta dentro.
	const range = useMemo(() => rangeForPeriod(null), []);

	const { config } = useScopedConfig(leaf.orgId);
	const { members, isLoading: membersLoading } = useMembers(leaf.orgId);
	const { totals, isLoading: energyLoading } = useEnergyData(range, leaf.orgId);

	if (membersLoading || energyLoading) {
		return <IncentivesConfigCardSkeleton cerNome={leaf.cerNome} />;
	}

	const data: IncentivesConfigCardData = {
		id: leaf.orgId,
		cerNome: leaf.cerNome,
		configNome: config.nome || leaf.configNome,
		comune: leaf.comune,
		areaCode: leaf.areaConvenzionale,
		condivisaKwh: Math.round(totals.totalCondivisa),
		sociCount: members.filter((m) => m.stato !== 'withdrawn').length,
	};

	return <IncentivesConfigCard data={data} onClick={onOpen} />;
}

function IncentivesPanel({ orgId }: { orgId: string }) {
	const [period, setPeriod] = useState<string | null>(null);
	// useEnergyData seeds its date filters on mount; each period needs its own
	// panel so production and all grid-energy totals refer to the same month.
	return (
		<IncentivesPeriodPanel
			key={period ?? 'current'}
			orgId={orgId}
			period={period}
			onPeriodChange={setPeriod}
		/>
	);
}

function IncentivesPeriodPanel({
	orgId,
	period,
	onPeriodChange,
}: {
	orgId: string;
	period: string | null;
	onPeriodChange: (period: string | null) => void;
}) {
	const range = useMemo(() => rangeForPeriod(period), [period]);
	// La soglia dagli impianti, non dal solo valore di CER (INB-313, INB-324).
	const { year, month } = useMemo(() => periodMonth(period), [period]);
	const soglia = useSogliaPreview(orgId, month, year);

	const { config, loading: configLoading } = useScopedConfig(orgId);
	const { totals, isLoading: energyLoading } = useEnergyData(range, orgId);

	return (
		<IncentivesGseView
			config={config}
			totals={totals}
			period={period}
			onPeriodChange={onPeriodChange}
			energyOverview={{
				// Energia prodotta = misura dei contatori M2 dedicati alle sezioni
				// d'impianto (RO CACER §2.2.3), come «Energia Prodotta» in
				// Dashboard. Prima era la telemetria degli inverter: su una
				// configurazione senza inverter collegati valeva 0 accanto a
				// migliaia di kWh immessi (INB-370).
				prodotta: totals.totalImmessa > 0 ? totals.totalImmessa : null,
			}}
			hasPnrrGrant={soglia?.hasPnrrGrant}
			quotaImmessaPnrr={soglia?.quotaImmessaPnrr}
			loading={configLoading || energyLoading}
		/>
	);
}
